import ReactDOM from "react-dom";
import { useEffect } from "react";
import moment from "moment";
import { CssBaseline } from "@mui/material";
import CreateVideo from "./CreateVideo";
import JoinVideo from "./JoinVideo";
import VideoStateProvider, { useVideoState } from "./state";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import PrivateRoute from "./components/video/PrivateRoute/PrivateRoute";
import theme from "./theme";
import "./types";
import { ChatProvider } from "./components/video/ChatProvider";
import { ParticipantProvider } from "./components/video/ParticipantProvider";
import { VideoProvider } from "./components/video/VideoProvider";
import useConnectionOptions from "./utils/useConnectionOptions/useConnectionOptions";
import UnsupportedBrowserWarning from "./components/video/UnsupportedBrowserWarning/UnsupportedBrowserWarning";
import Auth0ProviderWithHistory from "./state/auth0-provider-with-history";
import TwilioProvider from "./state/useApiTwilio/twilio-provider";
import Disconnected from "./Disconnected";
import Navbar from "./components/navbar/Navbar";
import Home from "./Home";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import Loading from "./components/Loading/Loading";
import { Box, ThemeProvider, Theme, StyledEngineProvider } from "@mui/material";
import "react-big-calendar/lib/css/react-big-calendar.css";
import UserStateProvider, { useUserState } from "./state/user";
import AppointmentsProvider from "./state/appointments";
import ChargesHistory from "./pages/ChargesHistory/ChargesHistory";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import "moment/locale/pt";
import "moment/locale/en-gb";
import "./global.scss";
import "./i18n";
import FiltersStateProvider from "./state/filters";
import CommissionsHistory from "./pages/CommissionsHistory/CommissionsHistory";
import RootCMDS from "./pages/CMDS";
import PaymentsAccount from "./pages/PaymentsAccount";
import PaymentConfigurationExplanation from "./pages/PaymentConfigurations/PaymentConfigurationExplanation";
import PaymentConfigurationForm from "./pages/PaymentConfigurations/PaymentConfigurationForm";
import PaymentConfigurationNextSteps from "./pages/PaymentConfigurations/PaymentConfigurationNextSteps";
import PaymentConfigurationDetail from "./pages/PaymentConfigurations/PaymentConfigurationDetail";
import PaymentConfigurationDocuments from "./pages/PaymentConfigurations/documents/PaymentConfigurationDocuments";
import PaymentConfigurationInReview from "./pages/PaymentConfigurations/PaymentConfigurationInReview";
import RequestPayment from "./pages/RequestPayment/RequestPayment";
import { withPaymentTerms } from "./components/payments/PaymentTermsGate/PaymentTermsGate";
import { withRegistrationTerms } from "./components/payments/PaymentTermsGate/RegistrationTermsGate";
import { PlanMedication } from "./pages/PlanMedication";
import { CancelVideo } from "./pages/CancelVideo";
import ErrorPage from "./components/ErrorPage";
import Utentes from "./pages/Patients/Patients";
import PatientDetail from "./pages/Patients/PatientDetail";
import ClinicalRecordsPage from "./pages/Patients/ClinicalRecordsPage";
import PersonalData from "./pages/PersonalData/PersonalData";
import ProfessionalData from "./pages/ProfessionalData/ProfessionalData";
import NotFound from "./pages/NotFound/NotFound";
import FloatingVideoWindow from "./components/video/FloatingVideoWindow/FloatingVideoWindow";
import {
  CONFIGURATION_DOCUMENTS_ROUTE,
  CONFIGURATION_IN_REVIEW_ROUTE,
  CONFIGURATION_NEXT_STEPS_ROUTE,
  CONFIGURATION_ROUTE,
  NEW_CONFIGURATION_ROUTE,
  PAYMENTS_TAB_ROUTE,
  PAYMENTS_WELCOME_ROUTE
} from "./utils/paymentsRoutes";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

// Every payments screen sits behind the terms gate, not only the account page —
// the payment request is reached straight from a patient card, which would
// otherwise walk right past it. Wrapped at module scope so the wrapped component
// type stays stable across renders and the page does not remount.
const ChargesHistoryGated = withPaymentTerms(ChargesHistory);
const CommissionsHistoryGated = withPaymentTerms(CommissionsHistory);
const PaymentConfigurationExplanationGated = withRegistrationTerms(
  PaymentConfigurationExplanation
);
const PaymentConfigurationFormGated = withRegistrationTerms(
  PaymentConfigurationForm
);
const PaymentConfigurationNextStepsGated = withPaymentTerms(
  PaymentConfigurationNextSteps
);
const PaymentConfigurationDetailGated = withPaymentTerms(
  PaymentConfigurationDetail
);
const PaymentConfigurationDocumentsGated = withPaymentTerms(
  PaymentConfigurationDocuments
);
const PaymentConfigurationInReviewGated = withPaymentTerms(
  PaymentConfigurationInReview
);
const RequestPaymentGated = withPaymentTerms(RequestPayment);

const VideoAppWrapper = ({ children }: { children: React.ReactNode }) => {
  const { setError } = useVideoState();
  const connectionOptions = useConnectionOptions();
  return (
    <VideoProvider options={connectionOptions} onError={setError}>
      <ParticipantProvider>
        <ChatProvider>{children}</ChatProvider>
      </ParticipantProvider>
    </VideoProvider>
  );
};

const CreateVideoApp = () => (
  <UnsupportedBrowserWarning>
    <CreateVideo />
  </UnsupportedBrowserWarning>
);

const JoinVideoApp = () => (
  <UnsupportedBrowserWarning>
    <JoinVideo />
  </UnsupportedBrowserWarning>
);

const RootApp = () => {
  const { isLoading, isAuthenticated, error } = useAuth0();
  const { loading, error: userError } = useUserState();
  const { i18n } = useTranslation();

  useEffect(() => {
    moment.locale(i18n.language.startsWith("pt") ? "pt" : "en-gb");
  }, [i18n.language]);

  if (isLoading || loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Loading />
      </Box>
    );
  }

  if (isAuthenticated && (error || userError)) {
    console.error(error || userError);
    return <ErrorPage />;
  }

  return (
    <VideoStateProvider>
      <VideoAppWrapper>
        <TwilioProvider>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale={i18n.language === "en" ? "en-gb" : "pt"}
          >
            <AppointmentsProvider>
              <Navbar />
              <FloatingVideoWindow />
              <Switch>
                <PrivateRoute exact path="/" component={Home} />
                <PrivateRoute
                  path="/charges-history"
                  component={ChargesHistoryGated}
                />
                <PrivateRoute
                  path="/commissions-history"
                  component={CommissionsHistoryGated}
                />
                {/* All of payments lives under one base. Order matters twice
                    over: the literal paths before the :configId ones, and every
                    configuration route before the tab route — which is exact,
                    so "/payments-account/configurations/new" is not read as the
                    tab named "configurations". */}
                <PrivateRoute
                  exact
                  path={PAYMENTS_WELCOME_ROUTE}
                  component={PaymentConfigurationExplanationGated}
                />
                <PrivateRoute
                  exact
                  path={NEW_CONFIGURATION_ROUTE}
                  component={PaymentConfigurationFormGated}
                />
                <PrivateRoute
                  exact
                  path={CONFIGURATION_NEXT_STEPS_ROUTE}
                  component={PaymentConfigurationNextStepsGated}
                />
                <PrivateRoute
                  exact
                  path={CONFIGURATION_DOCUMENTS_ROUTE}
                  component={PaymentConfigurationDocumentsGated}
                />
                <PrivateRoute
                  exact
                  path={CONFIGURATION_IN_REVIEW_ROUTE}
                  component={PaymentConfigurationInReviewGated}
                />
                <PrivateRoute
                  exact
                  path={CONFIGURATION_ROUTE}
                  component={PaymentConfigurationDetailGated}
                />
                <PrivateRoute
                  exact
                  path={PAYMENTS_TAB_ROUTE}
                  component={PaymentsAccount as any}
                />
                <PrivateRoute path="/cmds/:type" component={RootCMDS as any} />
                <PrivateRoute exact path="/patients" component={Utentes} />
                <PrivateRoute
                  exact
                  path="/patients/:id/clinical-records"
                  component={ClinicalRecordsPage}
                />
                <PrivateRoute
                  exact
                  path="/patients/:patientId/payments/new"
                  component={RequestPaymentGated}
                />
                <PrivateRoute
                  exact
                  path="/patients/:id"
                  component={PatientDetail}
                />
                <PrivateRoute path="/personal-data" component={PersonalData} />
                <PrivateRoute
                  path="/professional-data"
                  component={ProfessionalData}
                />
                <PrivateRoute path="/video" component={CreateVideoApp} />
                <Route
                  path="/app/state/disconnected"
                  component={Disconnected}
                />
                <Route path="/notfound" component={NotFound} />
                <Route
                  path="/invite-video/:URLRoomName?/:URLParticipantName/:URLDoctorName?"
                  component={JoinVideoApp}
                />
                {/* Catch-all: any unknown route renders the 404 page. */}
                <Route component={NotFound} />
              </Switch>
            </AppointmentsProvider>
          </LocalizationProvider>
        </TwilioProvider>
      </VideoAppWrapper>
    </VideoStateProvider>
  );
};

export const ReactApp = () => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Switch>
            <Route path="/plan-medication" component={PlanMedication} />
            <Route path="/cancel-video/:token" component={CancelVideo} />
            <Auth0ProviderWithHistory>
              <UserStateProvider>
                <FiltersStateProvider>
                  <RootApp />
                </FiltersStateProvider>
              </UserStateProvider>
            </Auth0ProviderWithHistory>
          </Switch>
        </Router>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

ReactDOM.render(<ReactApp />, document.getElementById("root"));
